import React from "react";
import { Image, Modal, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { CommonButton } from "../../../../common/CommonButton";
import { AppImages } from "../../../../utility/AppImages";

interface CreditConversionSuccessModalProps {
  visible: boolean;
  onClose: () => void;
  beltImage: any;
  beltName: string;
  beltAmount: number;
  creditsAmount: number;
  currencyValue: string;
}

export default function CreditConversionSuccessModal({
  visible,
  onClose,
  beltImage,
  beltName,
  beltAmount,
  creditsAmount,
  currencyValue,
}: CreditConversionSuccessModalProps) {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LinearGradient
          colors={["#111827", "#000"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            width: "90%",
            maxWidth: 600,
            padding: 24,
            borderRadius: 8,
            borderWidth: 1.6,
            borderColor: "rgba(34, 197, 94, 0.50)",
            alignItems: "center",
          }}
        >
          {/* Header with Close Button */}
          <View
            style={{
              width: "100%",
              borderBottomWidth: 0.5,
              borderBottomColor: "#D6D6D6",
              paddingBottom: 16,
              marginBottom: 24,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_24,
                { flex: 1 },
              ]}
            >
              Conversion Successful!
            </Text>
            <TouchableOpacity
              onPress={onClose}
              style={{
                padding: 4,
              }}
            >
              <Image
                source={AppImages.icCloseWhite}
                style={{
                  height: 24,
                  width: 24,
                  resizeMode: "contain",
                }}
              />
            </TouchableOpacity>
          </View>

          {/* Success Icon */}
          <View
            style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: "rgba(34, 197, 94, 0.2)",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 24,
            }}
          >
            <View
              style={{
                width: 50,
                height: 50,
                borderRadius: 25,
                backgroundColor: "#22C55E",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ color: AppColors.white, fontSize: 32 }}>✓</Text>
            </View>
          </View>

          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_16,
              { marginBottom: 32, textAlign: "center" },
            ]}
          >
            You've successfully converted
          </Text>

          {/* Conversion Visual */}
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              width: "100%",
              padding: 32,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-around",
              marginBottom: 24,
            }}
          >
            {/* From Belt */}
            <View style={{ alignItems: "center", gap: 12 }}>
              <Image
                source={beltImage}
                style={{
                  width: 100,
                  height: 100,
                  resizeMode: "contain",
                }}
              />
              <Text style={[AppFontStyle.BOLD_ANYBODY_18, { textAlign: "center" }]}>
                {beltAmount} {beltName} Belt{beltAmount !== 1 ? "s" : ""}
              </Text>
            </View>

            {/* Arrow */}
            <View>
              <Text
                style={{
                  fontSize: 40,
                  color: AppColors.white,
                  opacity: 0.5,
                }}
              >
                →
              </Text>
            </View>

            {/* Credits */}
            <View style={{ alignItems: "center", gap: 12 }}>
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_48,
                  { color: "#00D492", lineHeight: 56 },
                ]}
              >
                {creditsAmount}
              </Text>
              <Text style={[AppFontStyle.BOLD_ANYBODY_18, { textAlign: "center" }]}>
                Credits
              </Text>
            </View>
          </LinearGradient>

          {/* Table */}
          <View
            style={{
              width: "100%",
              marginBottom: 24,
              borderRadius: 8,
              backgroundColor: "rgba(31, 41, 55, 0.3)",
              overflow: "hidden",
            }}
          >
            {/* Table Header */}
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "rgba(55, 65, 81, 0.5)",
                paddingVertical: 12,
                paddingHorizontal: 16,
              }}
            >
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  { flex: 1, color: AppColors.gray_AFAFAF },
                ]}
              >
                Item
              </Text>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  { flex: 1, textAlign: "center", color: AppColors.gray_AFAFAF },
                ]}
              >
                Quantity
              </Text>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  { flex: 1, textAlign: "center", color: AppColors.gray_AFAFAF },
                ]}
              >
                Credit
              </Text>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  { flex: 1, textAlign: "right", color: AppColors.gray_AFAFAF },
                ]}
              >
                Value
              </Text>
            </View>

            {/* Table Row */}
            <View
              style={{
                flexDirection: "row",
                paddingVertical: 16,
                paddingHorizontal: 16,
              }}
            >
              <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { flex: 1 }]}>
                {beltName} Belt
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { flex: 1, textAlign: "center" },
                ]}
              >
                {beltAmount}
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { flex: 1, textAlign: "center" },
                ]}
              >
                {creditsAmount}
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { flex: 1, textAlign: "right", color: "#22C55E" },
                ]}
              >
                {currencyValue}
              </Text>
            </View>
          </View>

          {/* Info Text */}
          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_12,
              {
                marginBottom: 24,
                textAlign: "center",
                color: AppColors.gray_AFAFAF,
              },
            ]}
          >
            You can now redeem your credits for real money
          </Text>

          {/* Redeem Credits Button */}
          <LinearGradient
            colors={["#DC2626", "#991B1B"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              borderRadius: 6,
              alignSelf: "center",
            }}
          >
            <CommonButton
              buttonStyle={{
                paddingVertical: 10,
                paddingHorizontal: 24,
                backgroundColor: "transparent",
                borderRadius: 6,
              }}
              textStyle={[AppFontStyle.SEMIBOLD_ANYBODY_16, {}]}
              title="Redeem Credits"
              onTap={onClose}
            />
          </LinearGradient>
        </LinearGradient>
      </View>
    </Modal>
  );
}
